import React, { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { App } from 'antd';
import { getWeekendStatsLatest, purgeInvalidSnapshots } from '../../api/weekendStats';
import { AntPagination } from '../../components/TableCard';

const BLUE  = '#3B82F6';
const PINK  = '#EC4899';
const RED   = '#EF4444';
const SLATE = '#CBD5E1';
const AMBER = '#F59E0B';
const GREEN = '#10B981';
const RES_COLORS = ['#6366F1', '#0EA5E9', '#F59E0B', '#10B981'];

const pct = (n, d) => (d ? Math.round((Number(n) / Number(d)) * 100) : 0);
const fmt = (n) => (n === null || n === undefined ? '0' : Number(n).toLocaleString('fr-FR'));

/* ─── small sub-components ──────────────────────────────────────────────── */

const ACCENT_STYLES = {
  blue:  { border: '#BFDBFE', bg: '#DBEAFE', color: '#1D4ED8' },
  red:   { border: '#FECACA', bg: '#FEE2E2', color: '#B91C1C' },
  green: { border: '#A7F3D0', bg: '#D1FAE5', color: '#047857' },
  amber: { border: '#FDE68A', bg: '#FEF3C7', color: '#B45309' },
  slate: { border: '#CBD5E1', bg: '#F1F5F9', color: '#0F172A' },
};

function KpiCard({ label, value, sub, accent = 'slate' }) {
  const p = ACCENT_STYLES[accent] || ACCENT_STYLES.slate;
  const display = typeof value === 'string' ? value : fmt(value);
  return (
    <div className="stat-card" style={{ border: `1px solid ${p.border}`, background: p.bg }}>
      <div className="stat-label" style={{ color: p.color }}>{label}</div>
      <div className="stat-value" style={{ color: p.color, fontSize: 32 }}>{display}</div>
      {sub && <div style={{ fontSize: 11, color: p.color, opacity: 0.75, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="card" style={{ padding: '10px 14px', fontSize: 12, minWidth: 120 }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{label}</div>
      {payload.map((p) => (
        <div key={p.name} style={{ color: p.color || '#64748B' }}>
          {p.name} : <strong>{fmt(p.value)}</strong>
        </div>
      ))}
    </div>
  );
}

function ProgressBar({ value, max, color = BLUE }) {
  const w = max ? Math.round((Number(value || 0) / Number(max)) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ flex: 1, height: 6, background: '#E2E8F0', borderRadius: 999, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${w}%`, background: color, borderRadius: 999, transition: 'width 500ms ease' }} />
      </div>
      <span style={{ fontSize: 11, color: '#94A3B8', width: 32, textAlign: 'right' }}>{w}%</span>
    </div>
  );
}

function GenderPill({ h, f }) {
  return (
    <span style={{ display: 'inline-flex', gap: 6, fontSize: 12 }}>
      <span className="tag tag-blue">{h}H</span>
      <span style={{ background: '#FCE7F3', color: '#BE185D', fontWeight: 700, padding: '2px 8px', borderRadius: 999 }}>{f}F</span>
    </span>
  );
}

/* Legend dot for charts */
function Dot({ color }) {
  return <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 6, background: color, verticalAlign: 'middle', marginRight: 6 }} />;
}

/* ─── main component ─────────────────────────────────────────────────────── */

export default function WeekendStats() {
  const { notification } = App.useApp();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [purging, setPurging] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [detailPage, setDetailPage] = useState(1);
  const detailPageSize = 6;

  const fetchSnapshot = async () => {
    setLoading(true);
    try {
      const data = await getWeekendStatsLatest();
      setSnapshot(data || null);
    } catch (e) {
      notification.error({ message: 'Impossible de charger les statistiques week-end.', description: String(e?.message || e), placement: 'topRight' });
      setSnapshot(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchSnapshot(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => { setDetailPage(1); }, [activeTab]);

  const handlePurge = async () => {
    if (!window.confirm('Supprimer tous les snapshots invalides (dont slot_at n\'est pas un vendredi) ?')) return;
    setPurging(true);
    try {
      const result = await purgeInvalidSnapshots();
      if (result.deleted === 0) {
        notification.success({ message: 'Aucun snapshot invalide trouvé.', placement: 'topRight' });
      } else {
        notification.success({
          message: `${result.deleted} snapshot(s) invalide(s) supprimé(s).`,
          description: `IDs supprimés : ${(result.invalidIds || []).join(', ')}`,
          placement: 'topRight',
          duration: 8,
        });
        fetchSnapshot();
      }
    } catch (e) {
      notification.error({ message: 'Erreur lors de la purge.', description: String(e?.message || e), placement: 'topRight' });
    } finally {
      setPurging(false);
    }
  };

  const residences   = snapshot?.payload?.residences || [];
  const date         = snapshot?.payload?.date || snapshot?.date || '';

  const totalEtudiants = useMemo(() => residences.reduce((s, r) => s + Number(r.total || 0), 0), [residences]);
  const totalSortis    = useMemo(() => residences.reduce((s, r) => s + Number(r.sortis || 0), 0), [residences]);
  const totalPresents  = useMemo(() => totalEtudiants - totalSortis, [totalEtudiants, totalSortis]);
  const totalH         = useMemo(() => residences.reduce((s, r) => s + Number(r.hommes || 0), 0), [residences]);
  const totalF         = useMemo(() => residences.reduce((s, r) => s + Number(r.femmes || 0), 0), [residences]);

  const barDataTotal       = useMemo(() => residences.map(r => ({ name: r.name, Hommes: Number(r.hommes || 0), Femmes: Number(r.femmes || 0) })), [residences]);
  const barDataSortis      = useMemo(() => residences.map(r => ({ name: r.name, Sortis: Number(r.sortis || 0), Présents: Number(r.total || 0) - Number(r.sortis || 0) })), [residences]);
  const barDataSortisGenre = useMemo(() => residences.map(r => ({ name: r.name, Hommes: Number(r.sortisH || 0), Femmes: Number(r.sortisF || 0) })), [residences]);
  const pieData            = useMemo(() => [
    { name: `Hommes (${pct(totalH, totalEtudiants)}%)`, value: totalH },
    { name: `Femmes (${pct(totalF, totalEtudiants)}%)`, value: totalF },
  ], [totalH, totalF, totalEtudiants]);

  const filtered  = useMemo(() => (activeTab === 'all' ? residences : residences.filter(r => r.id === activeTab)), [activeTab, residences]);
  const pagedFiltered = useMemo(() => {
    const start = (detailPage - 1) * detailPageSize;
    return filtered.slice(start, start + detailPageSize);
  }, [detailPage, filtered]);
  const maxSortis = useMemo(() => Math.max(0, ...residences.map(r => Number(r.sortis || 0))), [residences]);

  if (loading && !snapshot) {
    return <div className="page"><div className="table-loading">Chargement des statistiques…</div></div>;
  }
  if (!snapshot) {
    return <div className="page"><div className="table-empty">Aucune donnée disponible.</div></div>;
  }

  return (
    <div className="page">

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div className="section-label" style={{ marginBottom: 0 }}>Statistiques — Administration</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {date && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#D1FAE5', border: '1px solid #A7F3D0', borderRadius: 10, padding: '5px 12px' }}>
              <span style={{ width: 8, height: 8, borderRadius: 999, background: GREEN, display: 'inline-block' }} />
              <span style={{ fontSize: 12, fontWeight: 700, color: '#047857' }}>{date}</span>
            </div>
          )}
          <button
            onClick={handlePurge}
            disabled={purging}
            title="Supprimer les snapshots dont la date n'est pas un vendredi"
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, padding: '5px 12px', borderRadius: 10, border: '1px solid #FECACA', background: purging ? '#FEF2F2' : '#FFF', color: '#B91C1C', cursor: purging ? 'not-allowed' : 'pointer', opacity: purging ? 0.7 : 1, transition: 'all 0.2s' }}
          >
            {purging ? '⏳ Purge…' : '🧹 Purger snapshots invalides'}
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="section-label" style={{ marginBottom: 8 }}>Vue globale</div>
      <div className="stats-grid">
        <KpiCard label="Total étudiants"    value={totalEtudiants} sub={`${residences.length} résidences`}                    accent="blue"  />
        <KpiCard label="Sortis ce week-end" value={totalSortis}    sub={`${pct(totalSortis, totalEtudiants)}% du total`}       accent="red"   />
        <KpiCard label="Présents"           value={totalPresents}  sub={`${pct(totalPresents, totalEtudiants)}% du total`}     accent="green" />
        <KpiCard label="Taux de sortie"     value={`${pct(totalSortis, totalEtudiants)}%`} sub="calculé vendredi 18h30"       accent="amber" />
      </div>

      {/* Charts — total */}
      <div className="section-label" style={{ marginTop: 24, marginBottom: 8 }}>Répartition — total étudiants</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 14 }}>

        <div className="card">
          <div className="card-inner">
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>Étudiants par résidence et sexe</div>
            <div style={{ display: 'flex', gap: 14, marginBottom: 10, fontSize: 12, color: '#64748B' }}>
              <span><Dot color={BLUE} />Hommes</span>
              <span><Dot color={PINK} />Femmes</span>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={barDataTotal} barSize={18} barGap={4}>
                <CartesianGrid vertical={false} stroke="#F1F5F9" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                <RechartsTooltip content={<CustomTooltip />} />
                <Bar dataKey="Hommes" fill={BLUE} radius={[4, 4, 0, 0]} />
                <Bar dataKey="Femmes" fill={PINK} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card">
          <div className="card-inner">
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>Répartition globale par sexe</div>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={80} dataKey="value" paddingAngle={3}>
                  <Cell fill={BLUE} />
                  <Cell fill={PINK} />
                </Pie>
                <Legend formatter={(v) => <span style={{ fontSize: 12, color: '#64748B' }}>{v}</span>} />
                <RechartsTooltip formatter={(v) => [fmt(v), '']} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Charts — sortis */}
      <div className="section-label" style={{ marginTop: 24, marginBottom: 8 }}>Répartition — étudiants sortis</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 14 }}>

        <div className="card">
          <div className="card-inner">
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>Sortis vs présents par résidence</div>
            <div style={{ display: 'flex', gap: 14, marginBottom: 10, fontSize: 12, color: '#64748B' }}>
              <span><Dot color={RED} />Sortis</span>
              <span><Dot color={SLATE} />Présents</span>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={barDataSortis} barSize={32}>
                <CartesianGrid vertical={false} stroke="#F1F5F9" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                <RechartsTooltip content={<CustomTooltip />} />
                <Bar dataKey="Sortis"   fill={RED}   stackId="a" />
                <Bar dataKey="Présents" fill={SLATE}  stackId="a" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card">
          <div className="card-inner">
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>Sortis par sexe et résidence</div>
            <div style={{ display: 'flex', gap: 14, marginBottom: 10, fontSize: 12, color: '#64748B' }}>
              <span><Dot color={BLUE} />Hommes</span>
              <span><Dot color={PINK} />Femmes</span>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={barDataSortisGenre} barSize={18} barGap={4}>
                <CartesianGrid vertical={false} stroke="#F1F5F9" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                <RechartsTooltip content={<CustomTooltip />} />
                <Bar dataKey="Hommes" fill={BLUE} radius={[4, 4, 0, 0]} />
                <Bar dataKey="Femmes" fill={PINK} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Detail table */}
      <div className="section-label" style={{ marginTop: 24, marginBottom: 8 }}>Détail par résidence et bâtiment</div>
      <div className="table-card">
        {/* Tab filter */}
        <div className="tab-bar" style={{ borderBottom: '0.5px solid rgba(0,0,0,0.06)', paddingBottom: 0 }}>
          <button className={`tab-btn${activeTab === 'all' ? ' active' : ''}`} onClick={() => setActiveTab('all')}>Toutes</button>
          {residences.map(r => (
            <button key={r.id} className={`tab-btn${activeTab === r.id ? ' active' : ''}`} onClick={() => setActiveTab(r.id)}>{r.name}</button>
          ))}
        </div>

        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Résidence / Bâtiment</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th style={{ textAlign: 'right' }}>Sortis</th>
                <th>H / F</th>
                <th style={{ textAlign: 'right', minWidth: 140 }}>Part</th>
              </tr>
            </thead>
            <tbody>
              {pagedFiltered.map((r, ri) => (
                <React.Fragment key={r.id}>
                  <tr style={{ background: 'rgba(0,0,0,0.02)' }}>
                    <td style={{ fontWeight: 700 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 10, height: 10, borderRadius: 999, background: RES_COLORS[ri % RES_COLORS.length], display: 'inline-block' }} />
                        {r.name}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>{fmt(r.total)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt(r.sortis)}</td>
                    <td><GenderPill h={r.sortisH} f={r.sortisF} /></td>
                    <td><ProgressBar value={r.sortis} max={maxSortis} color={RES_COLORS[ri % RES_COLORS.length]} /></td>
                  </tr>
                  {(r.batiments || []).map(b => (
                    <tr key={`${r.id}-${b.name}`}>
                      <td style={{ paddingLeft: 36, fontSize: 12, color: '#64748B' }}>{b.name}</td>
                      <td style={{ textAlign: 'right', color: '#CBD5E1', fontSize: 12 }}>—</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, fontSize: 12 }}>{fmt(b.sortis)}</td>
                      <td><GenderPill h={b.hommes} f={b.femmes} /></td>
                      <td><ProgressBar value={b.sortis} max={maxSortis} color={`${RES_COLORS[ri % RES_COLORS.length]}99`} /></td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ padding: '10px 12px', borderTop: '0.5px solid rgba(0,0,0,0.06)' }}>
          <AntPagination current={detailPage} total={filtered.length} pageSize={detailPageSize} onChange={setDetailPage} />
        </div>

        {/* Footer total */}
        <div style={{ padding: '12px 20px', background: '#0F172A', borderBottomLeftRadius: 12, borderBottomRightRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <span style={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.3, color: '#E2E8F0' }}>Total général</span>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 12, color: '#94A3B8' }}>
            <span>Inscrits : <strong style={{ color: '#fff' }}>{fmt(totalEtudiants)}</strong></span>
            <span>Sortis : <strong style={{ color: '#EF4444' }}>{fmt(totalSortis)}</strong></span>
            <span>Taux : <strong style={{ color: AMBER }}>{pct(totalSortis, totalEtudiants)}%</strong></span>
          </div>
        </div>
      </div>

    </div>
  );
}
